import './sentry';

import '~/styles/reset.css';
import '~/styles/fonts.css';
import '~/styles/global.css';
import '~/styles/themes.global.css';

import { Tooltip } from '@base-ui/react';
import { reactErrorHandler } from '@sentry/react';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import { NuqsAdapter } from 'nuqs/adapters/react-router/v7';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import invariant from 'tiny-invariant';

import { shouldCaptureErrorInSentry } from '~/api/errors';
import { CommandMenuProvider } from '~/providers/CommandMenuProvider/CommandMenuProvider';
import { QueryClientProvider } from '~/providers/QueryClientProvider/QueryClientProvider';
import { RouterProvider } from '~/providers/RouterProvider/RouterProvider';
import { ToastProvider } from '~/providers/ToastProvider/ToastProvider';
import { initTranslations } from '~/translations/i18n';
import { businessDays } from '~/utils/dayjs';

import { ColorThemeProvider } from './providers/ColorThemeProvider/ColorThemeProvider';
import { LanguageProvider } from './providers/LanguageProvider/LanguageProvider';

dayjs.extend(quarterOfYear);
dayjs.extend(customParseFormat);
dayjs.extend(businessDays);

// Load the translations
initTranslations();

// Render the app
const container = document.getElementById('root');
invariant(container, 'Missing root element in the DOM');

const reportRecoverableError = reactErrorHandler();

const root = createRoot(container, {
  onUncaughtError: reactErrorHandler((error, errorInfo) => {
    // eslint-disable-next-line no-console
    console.warn('Uncaught error', error, errorInfo.componentStack);
  }),
  onCaughtError: reactErrorHandler(),
  onRecoverableError: (error, errorInfo) => {
    // React wraps the original error as `cause`, so the `ignoreErrors` patterns in sentry.ts never match it
    if (error instanceof Error && error.cause instanceof Error && !shouldCaptureErrorInSentry(error.cause)) return;
    reportRecoverableError(error, errorInfo);
  },
});

root.render(
  <StrictMode>
    <NuqsAdapter>
      <QueryClientProvider>
        <LanguageProvider>
          <ColorThemeProvider>
            <CommandMenuProvider>
              <ToastProvider>
                <Tooltip.Provider delay={0}>
                  <RouterProvider />
                </Tooltip.Provider>
              </ToastProvider>
            </CommandMenuProvider>
          </ColorThemeProvider>
        </LanguageProvider>
      </QueryClientProvider>
    </NuqsAdapter>
  </StrictMode>,
);
